"use client";

import { useActionState, useEffect, useRef } from "react";
import { toast } from "sonner";

import { sendMessage } from "@/actions/send-mail";

const fieldClass =
  "w-full border-0 border-b border-hairline bg-transparent px-0 py-3 text-sm text-fg placeholder:text-fg-faint outline-none transition-colors focus:border-fg";

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-xs text-fg-muted">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-[oklch(0.7_0.19_22)]">{error}</p>}
    </div>
  );
}

const ContactForm = () => {
  const [state, action, isPending] = useActionState(sendMessage, null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (state?.success) {
      toast.success(state.success);
      formRef.current?.reset();
    }
  }, [state?.success]);

  useEffect(() => {
    if (state?.error) toast.error(state.error);
  }, [state?.error]);

  return (
    <form ref={formRef} action={action} className="flex flex-col gap-6">
      {/* Honeypot — hidden from humans; bots that fill it get dropped server-side. */}
      <input
        type="text"
        name="company"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute -left-[9999px] h-0 w-0 opacity-0"
      />

      <Field label="Name" error={state?.fullnameError}>
        <input
          type="text"
          required
          name="fullname"
          placeholder="Your name, your fame"
          className={fieldClass}
        />
      </Field>

      <Field label="Email" error={state?.emailError}>
        <input
          type="email"
          required
          name="email"
          placeholder="Where can I reach you back?"
          className={fieldClass}
        />
        <p className="mt-1 text-xs text-fg-faint">
          Temporary emails work too, unless you want to hear back.
        </p>
      </Field>

      <Field label="Message" error={state?.messageError}>
        <textarea
          required
          name="message"
          rows={3}
          placeholder="Your words, my inbox."
          className={`${fieldClass} resize-none`}
        />
      </Field>

      <button
        type="submit"
        disabled={isPending}
        className="inline-flex h-11 w-fit items-center rounded-full border border-hairline px-6 text-sm text-fg transition-colors hover:border-hairline-strong hover:bg-white/5 disabled:opacity-60"
      >
        {isPending ? "Sending…" : "Send message"}
      </button>
    </form>
  );
};

export default ContactForm;
